<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { UiIcon, UiText } from '@shopexperts/shared';
import type { AdminCategory, AdminSubcategory } from '@shopexperts/legacy-api';

const props = defineProps<{
  categories: AdminCategory[];
  searchQuery: string;
}>();

const emit = defineEmits<{
  editCategory: [category: AdminCategory];
  deleteCategory: [category: AdminCategory];
  addSubcategory: [parentCategory: AdminCategory];
  editSubcategory: [subcategory: AdminSubcategory & { category_id: number }];
  deleteSubcategory: [subcategory: AdminSubcategory & { category_id: number }];
}>();

const expandedIds = ref<Set<number>>(new Set());

const normalizedSearch = computed(() => props.searchQuery.trim().toLowerCase());

const filteredCategories = computed(() => {
  const query = normalizedSearch.value;
  if (!query) return props.categories;

  return props.categories.filter((cat) => {
    const catMatches = cat.name.toLowerCase().includes(query);
    const subMatches = cat.subcategories.some((sub) =>
      sub.name.toLowerCase().includes(query)
    );
    return catMatches || subMatches;
  });
});

watch(normalizedSearch, (query) => {
  if (!query) return;
  const idsToExpand = new Set<number>();
  for (const cat of props.categories) {
    const catMatches = cat.name.toLowerCase().includes(query);
    const subMatches = cat.subcategories.some((sub) =>
      sub.name.toLowerCase().includes(query)
    );
    if (catMatches || subMatches) {
      idsToExpand.add(cat.id);
    }
  }
  expandedIds.value = idsToExpand;
});

function toggleExpanded(id: number) {
  const next = new Set(expandedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIds.value = next;
}

function isExpanded(id: number) {
  return expandedIds.value.has(id);
}

function highlightMatch(text: string): string {
  const query = normalizedSearch.value;
  if (!query) return escapeHtml(text);
  const escaped = escapeHtml(text);
  const escapedQuery = escapeHtml(query);
  const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return escaped.replace(regex, '<mark class="cat-highlight">$1</mark>');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getFilteredSubcategories(category: AdminCategory) {
  const query = normalizedSearch.value;
  if (!query) return category.subcategories;
  const catNameMatches = category.name.toLowerCase().includes(query);
  if (catNameMatches) return category.subcategories;
  return category.subcategories.filter((sub) =>
    sub.name.toLowerCase().includes(query)
  );
}
</script>

<template>
  <div class="cat-tree">
    <div v-if="filteredCategories.length === 0" class="cat-tree__empty">
      <UiText as="p" variant="p-small" class="text-gray-400 text-center py-12">
        {{ searchQuery ? 'No categories match your search.' : 'No categories yet.' }}
      </UiText>
    </div>

    <TransitionGroup
      name="cat-list"
      tag="div"
      class="cat-tree__list"
    >
      <div
        v-for="category in filteredCategories"
        :key="category.id"
        class="cat-tree__node"
      >
        <!-- Category Header -->
        <div
          class="cat-tree__header group"
          :class="{ 'cat-tree__header--expanded': isExpanded(category.id) }"
          @click="toggleExpanded(category.id)"
        >
          <div class="cat-tree__header-left">
            <button
              class="cat-tree__chevron"
              :class="{ 'cat-tree__chevron--open': isExpanded(category.id) }"
              :aria-label="isExpanded(category.id) ? 'Collapse' : 'Expand'"
            >
              <UiIcon name="chevron-down" class="w-4 h-4" />
            </button>
            <div class="cat-tree__category-meta">
              <span
                class="cat-tree__category-name"
                v-html="highlightMatch(category.name)"
              />
              <span
                v-if="category.group_name"
                class="cat-tree__group-chip"
                v-html="highlightMatch(category.group_name)"
              />
            </div>
            <span class="cat-tree__count">
              {{ category.subcategories.length }}
            </span>
          </div>

          <div class="cat-tree__actions" @click.stop>
            <button
              class="cat-tree__action-btn cat-tree__action-btn--add"
              title="Add subcategory"
              @click="emit('addSubcategory', category)"
            >
              <UiIcon name="plus-square" class="w-4 h-4" />
            </button>
            <button
              class="cat-tree__action-btn"
              title="Edit category"
              @click="emit('editCategory', category)"
            >
              <UiIcon name="pencil" class="w-4 h-4" />
            </button>
            <button
              class="cat-tree__action-btn cat-tree__action-btn--delete"
              title="Delete category"
              @click="emit('deleteCategory', category)"
            >
              <UiIcon name="delete" class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Subcategories (inside same card, with tree lines) -->
        <Transition
          name="cat-collapse"
          @enter="onEnter"
          @after-enter="onAfterEnter"
          @leave="onLeave"
        >
          <div
            v-if="isExpanded(category.id) && getFilteredSubcategories(category).length > 0"
            class="cat-tree__body"
          >
            <div
              v-for="(sub, idx) in getFilteredSubcategories(category)"
              :key="sub.id"
              class="cat-tree__subcategory group"
              :class="{ 'cat-tree__subcategory--last': idx === getFilteredSubcategories(category).length - 1 }"
            >
              <!-- Vertical rail (runs top to center for last, top to bottom for others) -->
              <div class="cat-tree__rail" />
              <!-- Horizontal branch connector -->
              <div class="cat-tree__branch" />
              <span
                class="cat-tree__sub-name"
                v-html="highlightMatch(sub.name)"
              />
              <div class="cat-tree__actions" @click.stop>
                <button
                  class="cat-tree__action-btn"
                  title="Edit subcategory"
                  @click="emit('editSubcategory', { ...sub, category_id: category.id })"
                >
                  <UiIcon name="pencil" class="w-4 h-4" />
                </button>
                <button
                  class="cat-tree__action-btn cat-tree__action-btn--delete"
                  title="Delete subcategory"
                  @click="emit('deleteSubcategory', { ...sub, category_id: category.id })"
                >
                  <UiIcon name="delete" class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </TransitionGroup>
  </div>
</template>

<script lang="ts">
function onEnter(el: Element) {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = '0';
  htmlEl.style.overflow = 'hidden';
  htmlEl.offsetHeight; // force reflow
  htmlEl.style.height = htmlEl.scrollHeight + 'px';
}

function onAfterEnter(el: Element) {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = '';
  htmlEl.style.overflow = '';
}

function onLeave(el: Element) {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = htmlEl.scrollHeight + 'px';
  htmlEl.style.overflow = 'hidden';
  htmlEl.offsetHeight; // force reflow
  htmlEl.style.height = '0';
}
</script>

<style scoped>
.cat-tree__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cat-tree__node {
  background: white;
  border: 1px solid rgb(229 231 235);
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.cat-tree__node:hover {
  border-color: rgb(209 213 219);
  box-shadow: 0 1px 4px rgb(0 0 0 / 0.04);
}

/* Category header row */
.cat-tree__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;
}

.cat-tree__header:hover {
  background: rgb(249 250 251);
}

.cat-tree__header--expanded {
  border-bottom: 1px solid rgb(243 244 246);
}

.cat-tree__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cat-tree__category-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.cat-tree__chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: rgb(156 163 175);
  transition: all 0.2s ease;
  transform: rotate(-90deg);
  flex-shrink: 0;
  cursor: pointer;
  padding: 0;
}

.cat-tree__chevron--open {
  transform: rotate(0deg);
  color: rgb(107 114 128);
}

.cat-tree__chevron:hover {
  background: rgb(243 244 246);
  color: rgb(75 85 99);
}

.cat-tree__category-name {
  font-weight: 600;
  font-size: 14px;
  color: rgb(17 24 39);
  letter-spacing: -0.01em;
}

.cat-tree__group-chip {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgb(239 246 255);
  color: rgb(29 78 216);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cat-tree__count {
  font-size: 11px;
  font-weight: 500;
  color: rgb(156 163 175);
  background: rgb(243 244 246);
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

/* Action buttons */
.cat-tree__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.group:hover .cat-tree__actions {
  opacity: 1;
}

.cat-tree__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: rgb(156 163 175);
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.cat-tree__action-btn:hover {
  background: rgb(243 244 246);
  color: rgb(75 85 99);
}

.cat-tree__action-btn--add:hover {
  background: rgb(219 234 254);
  color: rgb(37 99 235);
}

.cat-tree__action-btn--delete:hover {
  background: rgb(254 226 226);
  color: rgb(220 38 38);
}

/* Subcategory body (inside same card) */
.cat-tree__body {
  transition: height 0.25s ease;
  position: relative;
}

.cat-tree__subcategory {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 18px 11px 56px;
  transition: background 0.12s ease;
  position: relative;
}

.cat-tree__subcategory:hover {
  background: rgb(249 250 251);
}

/* Vertical rail line */
.cat-tree__rail {
  position: absolute;
  left: 34px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgb(224 227 232);
}

.cat-tree__subcategory--last .cat-tree__rail {
  bottom: 50%;
}

/* Horizontal branch */
.cat-tree__branch {
  position: absolute;
  left: 34px;
  top: 50%;
  width: 12px;
  height: 1px;
  background: rgb(224 227 232);
}

.cat-tree__sub-name {
  font-size: 13.5px;
  color: rgb(55 65 81);
  font-weight: 400;
  letter-spacing: -0.005em;
  flex: 1;
  min-width: 0;
}

/* Highlight styling */
:deep(.cat-highlight) {
  background: rgb(253 230 138);
  color: rgb(113 63 18);
  border-radius: 2px;
  padding: 0 2px;
  font-style: normal;
}

/* Collapse transition */
.cat-collapse-enter-active,
.cat-collapse-leave-active {
  transition: height 0.25s ease, opacity 0.2s ease;
}

.cat-collapse-enter-from,
.cat-collapse-leave-to {
  opacity: 0;
}

/* List transition */
.cat-list-enter-active {
  transition: all 0.2s ease;
}

.cat-list-leave-active {
  transition: all 0.15s ease;
}

.cat-list-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.cat-list-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
